import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { connectorRegistryService, type Connector } from '../../services/connectors/connector-registry.service';
import { MainLayout } from '../../components/MainLayout';
import { useScopeContext } from '../../contexts/ScopeContext';
import { toast } from '../../services/toast.service';

export function ConnectorRegistryPage() {
  const scope = useScopeContext();
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showConnectionDialog, setShowConnectionDialog] = useState(false);
  const [connectionName, setConnectionName] = useState('');
  const [selectedConnector, setSelectedConnector] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    loadConnectors();
  }, []);

  const loadConnectors = async () => {
    try {
      setLoading(true);
      const data = await connectorRegistryService.getAllConnectors(scope.teamId);
      setConnectors(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connectors');
    } finally {
      setLoading(false);
    }
  };

  const filteredConnectors = connectors.filter(connector => {
    const matchesSearch = connector.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          connector.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || connector.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleConnectClick = (connectorId: string, connectorName: string) => {
    setSelectedConnector({ id: connectorId, name: connectorName });
    setConnectionName(`My ${connectorName}`);
    setShowConnectionDialog(true);
  };

  const handleConnectSubmit = async () => {
    if (!selectedConnector || !connectionName.trim()) return;

    try {
      const result = await connectorRegistryService.connectConnector(scope.teamId, {
        connectorId: selectedConnector.id,
        name: connectionName,
        credentials: {}
      });
      // Close dialog and reset state
      setShowConnectionDialog(false);
      setSelectedConnector(null);
      setConnectionName('');
      // Redirect to the connection details page
      route(`${scope.getNavigationPrefix()}/connections/${result.connectionId}`);
    } catch (err) {
      toast.error(`Failed to create connection: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleDialogCancel = () => {
    setShowConnectionDialog(false);
    setSelectedConnector(null);
    setConnectionName('');
  };

  if (loading) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div className="container mx-auto p-4">
            <div className="alert alert-error">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">
      <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">
        Team Connectors
      </h1>

      <div className="flex gap-4 mb-6">
        <input
          type="text"
          placeholder="Search connectors..."
          className="input input-bordered flex-1"
          value={searchTerm}
          onInput={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
        />
        <select
          className="select select-bordered"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory((e.target as HTMLSelectElement).value)}
        >
          <option value="all">All Categories</option>
          <option value="communication">Communication</option>
          <option value="storage">Storage</option>
          <option value="productivity">Productivity</option>
          <option value="development">Development</option>
          <option value="monitoring">Monitoring</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredConnectors.map((connector) => (
          <div key={connector.connectorId || connector.id} className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {connector.icon?.content && (
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                      {connector.icon.content.startsWith('<svg') ? (
                        <div
                          className="w-8 h-8"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          dangerouslySetInnerHTML={{
                            __html: connector.icon.content.replace(/<svg[^>]*>/, (match: string) =>
                              match
                                .replace(/width="[^"]*"/, 'width="32"')
                                .replace(/height="[^"]*"/, 'height="32"')
                                .replace(/<svg/, '<svg style="display: block; width: 100%; height: 100%; object-fit: contain;"')
                            )
                          }}
                        />
                      ) : (
                        <span className="text-2xl">{connector.icon.content}</span>
                      )}
                    </div>
                  )}
                  <h2 className="card-title">{connector.name}</h2>
                </div>
                {connector.isActive && (
                  <div className="badge badge-success">Active</div>
                )}
              </div>

              <p className="text-sm opacity-70">{connector.description || 'No description available'}</p>

              {connector.tags && connector.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {connector.tags.map((tag: string) => (
                    <span key={tag} className="badge badge-sm">{tag}</span>
                  ))}
                </div>
              )}

              <div className="card-actions justify-end mt-4">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleConnectClick(connector.connectorId || connector.id, connector.name)}
                >
                  Connect
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredConnectors.length === 0 && (
        <div className="text-center py-8">
          <p className="text-lg opacity-60">No connectors found</p>
        </div>
      )}

      {/* Connection Name Dialog */}
      {showConnectionDialog && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Create Connection</h3>
            <p className="mb-4">Enter a name for this connection:</p>
            <input
              type="text"
              className="input input-bordered w-full"
              value={connectionName}
              onInput={(e) => setConnectionName((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleConnectSubmit();
                }
              }}
              autoFocus
            />
            <div className="modal-action">
              <button className="btn" onClick={handleDialogCancel}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleConnectSubmit}
                disabled={!connectionName.trim()}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </MainLayout>
  );
}
